import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  BookOpenTextIcon,
  GearIcon,
  HeartIcon,
  ReceiptIcon,
  UserPlusIcon,
  XIcon,
} from '@phosphor-icons/react'

import type { FeatureFlagKeyType } from '@scrambl/shared'

import { InviteTeamMembersDialog } from '@/components/layout/InviteTeamMembersDialog'
import { Button } from '@/components/ui/button'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { UserAvatar } from '@/components/ui/user-avatar'
import { useIsMobile } from '@/hooks/use-mobile'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useCustomCollectionsQuery } from '@/hooks/queries/useCustomCollectionsQuery'
import { useFamilyStatuses } from '@/hooks/queries/useFamilyStatus'
import { prefetchModuleForPath } from '@/lib/prefetch/modulePrefetch'
import { cn } from '@/lib/utils'
import { isTauri } from '@/lib/utils/tauri'
import { useOrganizationStore } from '@/store/organization.store'
import { useUIPreferencesStore } from '@/store/ui-preferences.store'
import { getOrgPath } from '@/utils/org-paths'

import type { PersonalNavItem } from './personal-nav'
import { PERSONAL_NAV_SECTIONS } from './personal-nav'

/**
 * The personal space sidebar — a deliberately smaller, warmer surface than
 * AppSidebar. Hearth chrome comes from the `--sidebar-*` overrides under
 * `data-account-type="personal"` (colors.css); layout behavior (collapse,
 * mobile sheet, Tauri offset) reuses the shared sidebar primitives so the
 * title-bar toggle and ⌘\ shortcut keep working unchanged.
 */
export function PersonalSidebar() {
  const location = useLocation()
  const isMobile = useIsMobile()
  const { setOpenMobile, state } = useSidebar()
  const { checkFeatureFlag } = useFeatureFlags()
  const { checkPermission, isLoadingPermissions } = usePermissions()
  const currentOrganization = useOrganizationStore(
    org => org.currentOrganization,
  )
  const tauriGlassEnabled = useUIPreferencesStore(s => s.tauriGlassEnabled)
  const [showInviteDialog, setShowInviteDialog] = useState(false)

  const { data: familyStatuses } = useFamilyStatuses({
    enabled: Boolean(currentOrganization?.id),
  })

  // Bills gets a first-class nav entry once the Bills collection exists
  // (created from the blueprint gallery). Cheap: shares the collections cache.
  const { data: collections } = useCustomCollectionsQuery({
    enabled:
      Boolean(currentOrganization?.id) &&
      checkFeatureFlag('COLLECTIONS' as FeatureFlagKeyType),
  })
  const billsCollection = (collections ?? []).find(
    collection => collection.slug === 'bills',
  )

  const orgSlug = currentOrganization?.slug ?? ''
  const spaceName = currentOrganization?.name ?? 'Your space'
  const isDesktopTauri = isTauri() && !isMobile
  const isCollapsed = !isMobile && state === 'collapsed'

  const memberRole = currentOrganization?.userMembership?.role?.toUpperCase()
  const isSpaceAdmin = memberRole === 'OWNER' || memberRole === 'ADMIN'

  const getNavigationPath = (path: string) => getOrgPath(orgSlug, path)

  const isItemActive = (item: PersonalNavItem) => {
    const path = getNavigationPath(item.href)
    const active =
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    if (!active) return false
    return !(item.activeExcludes ?? []).some(excluded => {
      const excludedPath = getNavigationPath(excluded)
      return (
        location.pathname === excludedPath ||
        location.pathname.startsWith(`${excludedPath}/`)
      )
    })
  }

  const isItemVisible = (item: PersonalNavItem) => {
    if (item.featureFlag && !checkFeatureFlag(item.featureFlag)) return false
    if (item.permission && !isLoadingPermissions) {
      return checkPermission(item.permission.module, item.permission.action)
    }
    return true
  }

  const handleNavigate = () => {
    if (isMobile) setOpenMobile(false)
  }

  const handlePrefetch = (href: string) => {
    prefetchModuleForPath(href)
  }

  const familyMembers = (familyStatuses ?? []).slice(0, 5)

  return (
    <>
      <Sidebar
        variant="sidebar"
        collapsible={isMobile ? 'offcanvas' : 'icon'}
        tauriOffset={isTauri() && !isMobile}
        className={cn(
          'themed-sidebar ph-sidebar app-shell-sidebar bg-sidebar transition-colors duration-300',
          isDesktopTauri && 'tauri-desktop-sidebar',
          isDesktopTauri && tauriGlassEnabled && 'tauri-glass-sidebar',
          isMobile ? '!sm:p-0 !md:p-0 border-r-0 !p-0' : 'border-r-0',
        )}
      >
        {/* Mobile sheet header — wordmark + close (plain divs: the mobile
            Sheet strips SidebarHeader children) */}
        {isMobile && (
          <div className="bg-sidebar flex h-14 items-center justify-between px-4">
            <Link to="/organizations" onClick={handleNavigate}>
              <span className="font-kronaone text-sidebar-text/85 text-lg font-normal tracking-wide">
                scrambl
              </span>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setOpenMobile(false)}
              className="text-sidebar-text/70 hover:bg-sidebar-accent/15 h-8 w-8"
            >
              <XIcon className="h-5 w-5" weight="bold" />
              <span className="sr-only">Close menu</span>
            </Button>
          </div>
        )}

        {/* Space identity — the sidebar opens with who this space belongs to,
            not a module list */}
        <div
          className={cn(
            'ph-sidebar-identity',
            isCollapsed && 'ph-sidebar-identity-collapsed',
          )}
        >
          <Link
            to="/organizations"
            className="ph-sidebar-space"
            onClick={handleNavigate}
            title="Your spaces"
          >
            <span className="ph-sidebar-space-mark" aria-hidden>
              <HeartIcon weight="fill" />
            </span>
            {!isCollapsed && (
              <span className="ph-sidebar-space-text">
                <span className="ph-sidebar-space-name">{spaceName}</span>
                <span className="ph-sidebar-space-kind">Personal space</span>
              </span>
            )}
          </Link>
          {!isCollapsed && familyMembers.length > 1 && (
            <Link
              to={getNavigationPath('/team')}
              className="ph-sidebar-family"
              onClick={handleNavigate}
              title="Your family"
            >
              {familyMembers.map(member => (
                <span key={member.memberId} className="ph-sidebar-family-avatar">
                  <UserAvatar
                    user={{ id: member.userId, name: member.name }}
                    size="sm"
                  />
                  {member.status?.emoji && (
                    <span className="ph-sidebar-family-emoji" aria-hidden>
                      {member.status.emoji}
                    </span>
                  )}
                </span>
              ))}
            </Link>
          )}
        </div>

        <SidebarContent className="ph-sidebar-content">
          {PERSONAL_NAV_SECTIONS.map(section => {
            // Bills joins "Around the house" once its collection exists.
            const extraItems: PersonalNavItem[] =
              section.key === 'house' && billsCollection
                ? [
                    {
                      href: `/collections/${billsCollection.slug}`,
                      label: 'Bills',
                      icon: ReceiptIcon,
                    },
                  ]
                : []
            const items = [
              ...section.items.filter(isItemVisible),
              ...extraItems,
            ]
            if (items.length === 0) return null
            return (
              <div key={section.key} className="ph-sidebar-section">
                {section.title && !isCollapsed && (
                  <p className="ph-sidebar-section-label">{section.title}</p>
                )}
                <SidebarMenu className="ph-sidebar-menu">
                  {items.map(item => {
                    const active = isItemActive(item)
                    return (
                      <SidebarMenuItem key={item.href}>
                        <SidebarMenuButton
                          asChild
                          isActive={active}
                          tooltip={item.label}
                          className="ph-sidebar-item"
                        >
                          <Link
                            to={getNavigationPath(item.href)}
                            onClick={handleNavigate}
                            onMouseEnter={() => handlePrefetch(item.href)}
                            onFocus={() => handlePrefetch(item.href)}
                          >
                            <item.icon
                              className="ph-sidebar-item-icon"
                              weight={active ? 'fill' : 'regular'}
                            />
                            <span className="ph-sidebar-item-label group-data-[state=collapsed]:hidden">
                              {item.label}
                            </span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  })}
                </SidebarMenu>
              </div>
            )
          })}
        </SidebarContent>

        <SidebarFooter className="ph-sidebar-footer">
          {isSpaceAdmin && (
            <button
              type="button"
              className="ph-sidebar-invite"
              onClick={() => setShowInviteDialog(true)}
              title="Invite family"
            >
              <UserPlusIcon weight="bold" />
              {!isCollapsed && <span>Invite family</span>}
            </button>
          )}
          <div className="ph-sidebar-footer-links">
            <Link
              to={getNavigationPath('/help')}
              className="ph-sidebar-footer-link"
              onClick={handleNavigate}
              title="Help Center"
            >
              <BookOpenTextIcon />
              {!isCollapsed && <span>Help</span>}
            </Link>
            {isSpaceAdmin && (
              <Link
                to={getNavigationPath('/admin/settings')}
                className="ph-sidebar-footer-link"
                onClick={handleNavigate}
                title="Space settings"
              >
                <GearIcon />
                {!isCollapsed && <span>Space settings</span>}
              </Link>
            )}
          </div>
        </SidebarFooter>
      </Sidebar>

      <InviteTeamMembersDialog
        isOpen={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        onSuccess={() => setShowInviteDialog(false)}
      />
    </>
  )
}
